/**
 * TodaysClassCard - Card component for displaying a class session on the coach dashboard
 */

import { Badge, Box, Flex, Progress, Text, VStack } from "@chakra-ui/react"
import { FiClock, FiMapPin, FiUsers } from "react-icons/fi"

import type { TodaysClassSession } from "@/types/coach"

interface TodaysClassCardProps {
  classSession: TodaysClassSession
  onClick: () => void
}

export function TodaysClassCard({
  classSession,
  onClick,
}: TodaysClassCardProps) {
  const progressPercent =
    classSession.total_students > 0
      ? Math.round(
          (classSession.marked_count / classSession.total_students) * 100,
        )
      : 0

  const isComplete = classSession.marked_count === classSession.total_students

  return (
    <Box
      as="button"
      onClick={onClick}
      bg="white"
      borderRadius="lg"
      borderWidth="1px"
      borderColor={isComplete ? "green.200" : "gray.200"}
      p={4}
      w="full"
      textAlign="left"
      transition="all 0.2s"
      _hover={{
        borderColor: "blue.300",
        shadow: "md",
        transform: "translateY(-2px)",
      }}
      _focus={{
        outline: "2px solid",
        outlineColor: "blue.500",
        outlineOffset: "2px",
      }}
    >
      <VStack align="stretch" gap={3}>
        {/* Header with class name and level badge */}
        <Flex justify="space-between" align="flex-start">
          <Box>
            <Text fontWeight="semibold" fontSize="lg" color="gray.800">
              {classSession.class_name}
            </Text>
            <Badge colorPalette="blue" size="sm" mt={1}>
              {classSession.swim_level}
            </Badge>
          </Box>
          {isComplete && (
            <Badge colorPalette="green" size="sm">
              Complete
            </Badge>
          )}
        </Flex>

        {/* Time and location */}
        <Flex gap={4} color="gray.600" fontSize="sm">
          <Flex align="center" gap={1}>
            <FiClock size={14} />
            <Text>
              {classSession.start_time}
              {classSession.end_time && ` - ${classSession.end_time}`}
            </Text>
          </Flex>
          <Flex align="center" gap={1}>
            <FiMapPin size={14} />
            <Text>{classSession.location}</Text>
          </Flex>
        </Flex>

        {/* Attendance progress */}
        <Box>
          <Flex justify="space-between" align="center" mb={1}>
            <Flex align="center" gap={1} color="gray.600" fontSize="sm">
              <FiUsers size={14} />
              <Text>
                {classSession.marked_count} / {classSession.total_students}{" "}
                marked
              </Text>
            </Flex>
            <Text fontSize="xs" color="gray.500">
              {progressPercent}%
            </Text>
          </Flex>
          <Progress.Root value={progressPercent} size="sm">
            <Progress.Track bg="gray.100" borderRadius="full">
              <Progress.Range
                bg={isComplete ? "green.500" : "blue.500"}
                borderRadius="full"
              />
            </Progress.Track>
          </Progress.Root>
        </Box>
      </VStack>
    </Box>
  )
}
